import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Globe2,
  Sparkles,
  Target,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";

import whyZulfah from "../../../utils/static-datas/why-zulfah";

const WhyZulfah = () => {
  const icons = [Target, Zap, Users, TrendingUp];

  return (
    <section className="relative overflow-hidden bg-[#F7F7F5] py-10 sm:py-32 md:py-40">

      {/* =====================================================
          BACKGROUND
      ====================================================== */}

      <div className="pointer-events-none absolute inset-0">

        {/* Cyan glow */}
        <div className="absolute -right-40 top-20 h-[500px] w-[500px] rounded-full bg-[#59B5CD]/10 blur-[130px]" />

        {/* Gold glow */}
        <div className="absolute -left-40 bottom-0 h-[450px] w-[450px] rounded-full bg-[#ECB32C]/10 blur-[130px]" />

        {/* Giant background word */}
        <div className="absolute right-[-5%] top-[20%] hidden select-none font-[ubuntu] text-[18vw] font-black tracking-[-0.1em] text-black/[0.025] lg:block">
          WHY
        </div>

      </div>


      <div className="relative mx-auto max-w-[1400px] px-6 sm:px-8 md:px-12 lg:px-20">


        {/* =====================================================
            HEADER
        ====================================================== */}

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8 }}
          className="grid gap-12 lg:grid-cols-[0.7fr_1.3fr]"
        >

          {/* Label */}

          <div>

            <div className="flex items-center gap-3">

              <span className="h-2 w-2 rounded-full bg-[#CF385D]" />

              <span className="text-[9px] uppercase tracking-[0.4em] text-gray-400">
                02 · Why Zulfah
              </span>

            </div>

            <div className="mt-10 hidden items-center gap-3 lg:flex">

              <div className="h-px w-16 bg-black/10" />

              <span className="text-[9px] uppercase tracking-[0.3em] text-gray-300">
                Learn · Build · Become
              </span>

            </div>

          </div>


          {/* Main statement */}

          <div>

            <h2 className="font-[ubuntu] text-5xl font-bold leading-[0.88] tracking-[-0.065em] text-gray-950 sm:text-6xl md:text-7xl lg:text-[6.5rem]">

              Not just
              <br />

              a tech school.

              <br />

              <span className="text-gray-300">
                A launchpad.
              </span>

            </h2>

            <p className="mt-10 max-w-3xl text-base leading-8 text-gray-500 md:text-lg">

              Africa has extraordinary talent.

              <span className="text-gray-900">
                {" "}What too many people lack is access.
              </span>

              {" "}Access to knowledge, experienced mentors, practical
              environments and opportunities to build.

            </p>

          </div>

        </motion.div>


        {/* =====================================================
            AFRICA → GLOBAL STATEMENT
        ====================================================== */}

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9 }}
          className="relative mt-24 overflow-hidden bg-[#080B10] px-7 py-12 text-white sm:px-10 md:px-16 md:py-16"
        >

          {/* Background glow */}

          <div className="pointer-events-none absolute right-[-150px] top-[-200px] h-[500px] w-[500px] rounded-full bg-[#59B5CD]/15 blur-[120px]" />

          <div className="pointer-events-none absolute bottom-[-200px] left-[-100px] h-[450px] w-[450px] rounded-full bg-[#CF385D]/10 blur-[120px]" />


          <div className="relative flex flex-col justify-between gap-12 lg:flex-row lg:items-end">

            <div>

              <div className="flex items-center gap-3">

                <Globe2 className="h-4 w-4 text-[#59B5CD]" />

                <span className="text-[9px] uppercase tracking-[0.4em] text-white/35">
                  Africa → Global
                </span>

              </div>

              <h3 className="mt-8 max-w-4xl font-[ubuntu] text-4xl font-bold leading-[0.92] tracking-[-0.055em] sm:text-5xl md:text-7xl">

                Where African talent
                <br />

                <span className="text-white/20">
                  meets global opportunity.
                </span>

              </h3>

            </div>


            <div className="max-w-sm lg:pb-2">

              <p className="text-sm leading-7 text-white/40">
                We are building a generation of technology professionals
                capable of competing, collaborating and creating on a global
                stage.
              </p>

              <div className="mt-7 flex items-center gap-3">

                <span className="h-px w-10 bg-[#ECB32C]" />

                <span className="text-[9px] uppercase tracking-[0.3em] text-white/30">
                  The Zulfah mission
                </span>

              </div>

            </div>

          </div>

        </motion.div>


        {/* =====================================================
            FEATURE GRID
        ====================================================== */}

        <div className="mt-5 grid gap-3 md:grid-cols-2">

          {whyZulfah.map((each, index) => {

            const Icon = icons[index % icons.length];

            return (
              <motion.article
                key={index}
                initial={{
                  opacity: 0,
                  y: 35,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                viewport={{
                  once: true,
                  amount: 0.15,
                }}
                transition={{
                  duration: 0.7,
                  delay: index * 0.08,
                }}
                whileHover={{
                  y: -7,
                }}
                className="group relative overflow-hidden border border-black/10 bg-white p-8 transition duration-500 md:p-12"
              >

                {/* Hover gradient */}

                <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#59B5CD]/0 via-transparent to-[#ECB32C]/0 opacity-0 transition duration-700 group-hover:from-[#59B5CD]/10 group-hover:to-[#ECB32C]/10 group-hover:opacity-100" />


                {/* Accent line */}

                <div
                  className="absolute left-0 top-0 h-1 w-0 transition-all duration-500 group-hover:w-full"
                  style={{
                    backgroundColor:
                      index === 1
                        ? "#ECB32C"
                        : index === 2
                        ? "#CF385D"
                        : "#59B5CD",
                  }}
                />


                {/* Top */}

                <div className="relative flex items-start justify-between">

                  <span className="font-[ubuntu] text-5xl font-bold tracking-[-0.05em] text-black/[0.05] transition group-hover:text-black/10">
                    0{index + 1}
                  </span>


                  <div className="flex h-12 w-12 items-center justify-center rounded-full border border-black/10 transition duration-500 group-hover:rotate-6 group-hover:bg-[#080B10] group-hover:text-white">

                    <Icon className="h-5 w-5" />

                  </div>

                </div>


                {/* Content */}

                <div className="relative mt-20">

                  <h3 className="font-[ubuntu] text-3xl font-bold tracking-[-0.045em] text-gray-950 md:text-4xl">
                    {each.heading}
                  </h3>

                  <p className="mt-5 max-w-lg text-sm leading-7 text-gray-500">
                    {each.details}
                  </p>

                </div>


                {/* Bottom */}

                <div className="relative mt-10 flex items-center justify-between border-t border-black/10 pt-5">

                  <span className="text-[9px] uppercase tracking-[0.25em] text-gray-300 transition group-hover:text-gray-500">
                    Zulfah advantage
                  </span>

                  <ArrowUpRight className="h-4 w-4 text-gray-300 transition duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-gray-950" />

                </div>

              </motion.article>
            );

          })}

        </div>


        {/* =====================================================
            BIG IMPACT MESSAGE
        ====================================================== */}

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-28 grid gap-12 lg:grid-cols-[1fr_0.8fr] lg:items-end"
        >

          <div>

            <div className="flex items-center gap-3">

              <Sparkles className="h-4 w-4 text-[#ECB32C]" />

              <span className="text-[9px] uppercase tracking-[0.4em] text-gray-400">
                The bigger picture
              </span>

            </div>

            <h3 className="mt-7 max-w-5xl font-[ubuntu] text-4xl font-bold leading-[0.95] tracking-[-0.055em] sm:text-5xl md:text-7xl">

              We are not preparing people
              <br />

              <span className="text-gray-300">
                for yesterday's jobs.
              </span>

            </h3>

          </div>


          <div className="lg:pb-2">

            <p className="text-sm leading-8 text-gray-500 md:text-base">

              Technology is changing faster than traditional education.
              That's why our focus is on practical skills, problem solving,
              adaptability and the confidence to keep learning.

            </p>

            <div className="mt-8 flex items-center gap-4">

              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#ECB32C]">

                <ArrowUpRight className="h-4 w-4 text-black" />

              </div>

              <span className="text-[9px] uppercase tracking-[0.25em] text-gray-400">
                Built for the future
              </span>

            </div>

          </div>

        </motion.div>


        {/* =====================================================
            FINAL CTA PANEL
        ====================================================== */}

        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative mt-28 overflow-hidden bg-[#ECB32C] px-8 py-14 md:px-16 md:py-20"
        >

          {/* Decorative giant number */}

          <div className="pointer-events-none absolute right-[-20px] top-[-70px] font-[ubuntu] text-[16rem] font-black leading-none tracking-[-0.1em] text-black/[0.05]">
            01
          </div>


          <div className="relative flex flex-col justify-between gap-10 md:flex-row md:items-end">

            <div>

              <span className="text-[9px] uppercase tracking-[0.4em] text-black/40">
                Your advantage starts here
              </span>

              <h3 className="mt-6 max-w-4xl font-[ubuntu] text-4xl font-bold leading-[0.9] tracking-[-0.055em] sm:text-5xl md:text-6xl">

                Don't just learn technology.
                <br />

                <span className="text-black/30">
                  Learn to create with it.
                </span>

              </h3>

            </div>


            <a
              href="/get-started"
              className="group inline-flex shrink-0 items-center gap-4 bg-[#080B10] px-7 py-5 text-[10px] font-semibold uppercase tracking-[0.25em] text-white transition hover:-translate-y-1 hover:bg-[#59B5CD]"
            >
              Start your journey

              <ArrowUpRight className="h-4 w-4 transition group-hover:translate-x-1 group-hover:-translate-y-1" />

            </a>

          </div>

        </motion.div>

      </div>

    </section>
  );
};

export default WhyZulfah;